import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Chip, Alert,
  Tabs, Tab,
} from '@mui/material';
import { auth } from '../../firebase';

const API = process.env.REACT_APP_API_BASE_URL;
const BID = process.env.REACT_APP_DECORMANAGER_BUILDER_ID;

const IpAccessDialog = ({
  open, onClose, apiKey, onUpdated,
}) => {
  const [tab, setTab] = useState(0);
  const [allowedIps, setAllowedIps] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (apiKey) {
      setAllowedIps(apiKey.allowedIps || []);
      setBlockedIps(apiKey.blockedIps || []);
      setNewIp('');
      setError('');
    }
  }, [apiKey]);

  const isValidIp = (ip) => {
    const ipv4 = /^(\d{1,3}\.){3}\d{1,3}$/;
    const cidr = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
    return ipv4.test(ip) || cidr.test(ip);
  };

  const handleAdd = () => {
    const ip = newIp.trim();
    if (!ip) return;
    if (!isValidIp(ip)) {
      setError('Enter a valid IPv4 address (e.g. 192.168.1.1)');
      return;
    }
    const list = tab === 0 ? allowedIps : blockedIps;
    if (list.includes(ip)) {
      setError('IP already in list');
      return;
    }
    if (tab === 0) {
      setAllowedIps([...allowedIps, ip]);
    } else {
      setBlockedIps([...blockedIps, ip]);
    }
    setNewIp('');
    setError('');
  };

  const handleRemove = (ip, listType) => {
    if (listType === 'allowed') {
      setAllowedIps(allowedIps.filter((i) => i !== ip));
    } else {
      setBlockedIps(blockedIps.filter((i) => i !== ip));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const token = await auth.currentUser.getIdToken();
      const resp = await fetch(
        `${API}/auth/external-api-keys/update?builder_id=${BID}&authorization=${token}&key_id=${apiKey.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ allowedIps, blockedIps }),
        },
      );
      const result = await resp.json();
      if (result.success) {
        onUpdated();
        onClose();
      } else {
        setError(result.error?.message || 'Failed to update');
      }
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  if (!apiKey) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        IP Access Control:
        {' '}
        {apiKey.label}
      </DialogTitle>
      <DialogContent>
        {
apiKey.lastIp && (
  <Alert severity="info" sx={{ mb: 2 }}>
    Last seen IP:
    {' '}
    <strong>{apiKey.lastIp}</strong>
  </Alert>
)
}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab
            label={`Allowlist (${allowedIps.length})`}
          />
          <Tab
            label={`Blocklist (${blockedIps.length})`}
          />
        </Tabs>

        {
tab === 0 && (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      When set, only these IPs can use this key. Leave empty to allow all IPs.
    </Typography>
    <Box sx={
{
  display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, minHeight: 32,
}
}
    >
      {
allowedIps.map((ip) => (
  <Chip
    key={ip}
    label={ip}
    onDelete={() => handleRemove(ip, 'allowed')}
    color="primary"
    variant="outlined"
    size="small"
  />
))
}
      {
allowedIps.length === 0 && (
  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
    No restrictions — all IPs allowed
  </Typography>
)
}
    </Box>
  </Box>
)
}

        {
tab === 1 && (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      These IPs are always denied, regardless of the allowlist.
    </Typography>
    <Box sx={
{
  display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, minHeight: 32,
}
}
    >
      {
blockedIps.map((ip) => (
  <Chip
    key={ip}
    label={ip}
    onDelete={() => handleRemove(ip, 'blocked')}
    color="error"
    variant="outlined"
    size="small"
  />
))
}
      {
blockedIps.length === 0 && (
  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
    No IPs blocked
  </Typography>
)
}
    </Box>
  </Box>
)
}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label="IP address"
            placeholder="e.g. 192.168.1.1"
            value={newIp}
            onChange={(e) => { setNewIp(e.target.value); setError(''); }}
            onKeyDown={handleKeyDown}
            size="small"
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" onClick={handleAdd}>
            Add
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IpAccessDialog;
